/* eslint-disable no-console */
import type {
   WebSocketAnswerMessage,
   WebSocketChatConnection,
   WebSocketProgressMessage,
} from '@/services/websocket-chat-service';
import type {
   ApalumaChatMessage,
   LumaThought,
} from '@/types/api-response/chat-session-types';
import { saveAssistantMessage } from '@/services/chat-threads-service';
import { createWebSocketChatConnection } from '@/services/websocket-chat-service';
import { useAuthStore } from '@/stores/auth-store';
import { deriveThought, stageLabel } from '@/utils/luma-thoughts';

/**
 * One live Luma conversation per thread.
 *
 * Luma has no database of its own, so the browser is what persists an answer —
 * which means a socket that dies before the answer arrives loses it for good.
 * Connections are therefore keyed by thread rather than held in a single slot:
 * starting a second conversation no longer displaces the first, and neither one
 * is tied to whatever happens to be on screen.
 *
 * The registry lives in the composable's closure, so each caller
 * (`chat-session-store`, `project-chat-store`) keeps its own — they are
 * separate stores with separate lifetimes and shouldn't cancel each other.
 */
export function useWebSocketChat() {
   const authStore = useAuthStore();

   const connections = new Map<number, WebSocketChatConnection>();

   function getWsBaseUrl(): string {
      const url = import.meta.env.VITE_LUMA_AGENT_WS_URL || '';
      console.log('[useWebSocketChat] getWsBaseUrl:', url);
      return url;
   }

   function getApiKey(): string {
      const apiKey = authStore.userApiKey?.api_key || '';
      console.log('[useWebSocketChat] getApiKey:', apiKey ? 'present' : 'missing');
      return apiKey;
   }

   /**
    * Send a message via WebSocket after the user message has been saved to the backend.
    * The service queues messages until the connection opens, so we can call sendMessage immediately.
    *
    * Progress and completion updates flow through the onUpdate callback, which writes
    * directly to the message object. No store-level side-channel refs needed.
    */
   function sendViaWebSocket(
      question: string,
      threadId: number,
      onUpdate: (partial: Partial<ApalumaChatMessage>) => void,
      docids?: string[],
   ): { promise: Promise<WebSocketAnswerMessage>; cancel: () => void; close: () => void } {
      console.log('[sendViaWebSocket] Called with:', { question: `${question.substring(0, 50)}...`, threadId, docids });
      let resolvePromise: (value: WebSocketAnswerMessage) => void;
      let rejectPromise: (reason: Error) => void;
      let settled = false;

      const promise = new Promise<WebSocketAnswerMessage>((resolve, reject) => {
         resolvePromise = (value) => {
            settled = true;
            resolve(value);
         };
         rejectPromise = (reason) => {
            settled = true;
            reject(reason);
         };
      });

      /**
       * Luma's reasoning trail for this turn. Owned by the connection because
       * that is exactly its lifetime — one question, one trail, discarded when
       * the thread reloads. Replaced rather than mutated on each append so the
       * bubbles already on screen can never change under the reader.
       */
      let thoughts: LumaThought[] = [];

      const conn = createWebSocketChatConnection({
         wsBaseUrl: getWsBaseUrl(),
         apiKey: getApiKey(),
         threadId,
         docids,
         callbacks: {
            onProgress(msg: WebSocketProgressMessage) {
               console.log('[sendViaWebSocket] Progress:', msg.stage, msg.message);

               const thought = deriveThought(thoughts, msg.stage, msg.message || '');
               if (thought) {
                  thoughts = [...thoughts, thought];
               }

               // `progressStage` still drives the single status line in document
               // and project chat, which this same path feeds. `progressDetail`
               // has no reader left now the main chat streams a trail instead —
               // kept populated so those surfaces can pick it up unchanged.
               onUpdate({
                  progressStage: stageLabel(msg.stage),
                  progressDetail: msg.message || '',
                  ...(thought && { thoughts }),
               });
            },
            onAnswer(msg: WebSocketAnswerMessage) {
               console.log('[sendViaWebSocket] Answer received:', { type: msg.type, contentLength: msg.response?.length });
               const isCancelled = msg.type === 'cancelled';
               const content = msg.response || '';
               const rawDocs = msg.reference ?? [];
               const reference = rawDocs?.length ? rawDocs : undefined;
               const filter = msg.filter?.length ? msg.filter : undefined;
               onUpdate({
                  content,
                  status: isCancelled ? 'stopped' : 'done',
                  progressStage: '',
                  progressDetail: '',
                  ...(reference && {
                     reference,
                     number_of_documents: msg.number_of_documents
                        ?? new Set(reference.map(r => r.document_id)).size,
                  }),
                  ...(filter && { filter }),
               });

               if (!isCancelled && content.trim()) {
                  saveAssistantMessage(threadId, content, reference, filter)
                     .catch(err => console.error('Failed to save assistant response:', err));
               }

               resolvePromise!(msg);
            },
            onError(error: string) {
               console.error('[sendViaWebSocket] Error:', error);

               // A late error is teardown noise, not a failed turn. The agent
               // reliably sends one ("No active processing to cancel") after the
               // answer has already landed, and marking the message errored at
               // that point mislabels a good answer — losing its copy and
               // feedback controls. `onClose` guards the same way.
               if (settled) {
                  console.warn('[sendViaWebSocket] Ignoring error after answer:', error);
                  return;
               }

               onUpdate({ status: 'error', progressStage: '', progressDetail: '' });
               rejectPromise!(new Error(error));
            },
            onClose(_code: number, reason: string) {
               console.log('[sendViaWebSocket] Connection closed:', { code: _code, reason });
               // Only retire the slot if it is still ours — a newer turn on the
               // same thread may already have claimed it.
               if (connections.get(threadId) === conn) {
                  connections.delete(threadId);
               }
               if (!settled) {
                  rejectPromise!(new Error(reason || 'WebSocket closed before response'));
               }
            },
         },
      });

      // A second turn on the same thread supersedes the first; different
      // threads coexist.
      connections.get(threadId)?.close();
      connections.set(threadId, conn);

      console.log('[sendViaWebSocket] Calling conn.sendMessage');
      // Messages are queued by the service until the WS connection opens
      conn.sendMessage(question);

      return {
         promise,
         cancel: () => conn.cancel(),
         close: () => {
            conn.close();
            if (connections.get(threadId) === conn) {
               connections.delete(threadId);
            }
         },
      };
   }

   /**
    * Ask the agent to stop working on one thread.
    *
    * Deliberately narrow: cancelling is now something a user does to a specific
    * conversation, never a side effect of leaving a screen.
    */
   function cancelThread(threadId: number) {
      connections.get(threadId)?.cancel();
   }

   /** Drop one thread's connection without waiting for the agent. */
   function closeThread(threadId: number) {
      connections.get(threadId)?.close();
      connections.delete(threadId);
   }

   /** Tear down everything — for logout and store reset, not navigation. */
   function closeAll() {
      for (const conn of connections.values())
         conn.close();
      connections.clear();
   }

   /** Whether a thread still has a turn in flight. */
   function isThreadActive(threadId: number): boolean {
      return connections.has(threadId);
   }

   return {
      sendViaWebSocket,
      cancelThread,
      closeThread,
      closeAll,
      isThreadActive,
   };
}
